import {
  SessionLearningContext,
  InteractionFeedback,
  RealTimeLearningMetadata,
  AgentType,
  Client
} from '@/types';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Real-time Learning Service
 * Enables immediate learning and adaptation from user feedback within chat sessions
 */
class RealTimeLearningService {
  private sessionContexts: Map<string, SessionLearningContext> = new Map();
  private learningEnabled: boolean = true;
  private currentUserId: string | null = null;

  /**
   * Set current user for Firestore operations
   */
  setCurrentUser(userId: string): void {
    this.currentUserId = userId;
  }

  /**
   * Process immediate feedback and generate real-time adaptations
   */
  async processFeedback(
    conversationId: string,
    messageId: string,
    feedback: InteractionFeedback,
    agentType: AgentType,
    originalResponse: string,
    client?: Client,
    userId?: string
  ): Promise<{
    adaptations: Record<string, any>;
    suggestions: string[];
    confidenceBoost: number;
  }> {
    if (!this.learningEnabled) {
      return { adaptations: {}, suggestions: [], confidenceBoost: 0 };
    }

    const sessionContext = this.getOrCreateSessionContext(conversationId);
    
    // Record the feedback
    sessionContext.feedback.push({
      messageId,
      timestamp: new Date().toISOString(),
      feedback,
      agentType,
      originalResponse
    });

    // Analyze feedback for immediate improvements
    const analysis = this.analyzeFeedback(feedback, originalResponse, sessionContext);
    
    // Generate real-time adaptations
    const adaptations = await this.generateAdaptations(analysis, agentType, client);
    
    // Update session patterns
    this.updateSessionPatterns(sessionContext, analysis);
    
    // Persist to Firestore
    if (userId || this.currentUserId) {
      await this.persistFeedbackToFirestore(
        userId || this.currentUserId!,
        conversationId,
        messageId,
        feedback,
        agentType,
        originalResponse,
        { adaptations, suggestions: analysis.suggestions, confidenceBoost: analysis.confidenceBoost },
        client?.id
      );
      
      await this.persistSessionToFirestore(
        userId || this.currentUserId!,
        conversationId,
        sessionContext,
        client?.id
      );
    }
    
    return {
      adaptations,
      suggestions: analysis.suggestions,
      confidenceBoost: analysis.confidenceBoost
    };
  }

  /**
   * Get learning context for enhancing agent prompts
   */
  getLearningContext(conversationId: string, agentType: AgentType): {
    adaptations: Record<string, any>;
    patterns: any;
    metadata: RealTimeLearningMetadata;
  } {
    const sessionContext = this.sessionContexts.get(conversationId);
    
    if (!sessionContext) {
      return {
        adaptations: {},
        patterns: {},
        metadata: {
          adaptedFromFeedback: false,
          sessionCorrections: 0,
          confidenceBoost: 0,
          lastAdaptation: '',
          learningHistory: []
        }
      };
    }

    // Filter feedback relevant to this agent type
    const relevantFeedback = sessionContext.feedback.filter(f => f.agentType === agentType);
    
    return {
      adaptations: sessionContext.activeAdaptations,
      patterns: sessionContext.patterns,
      metadata: {
        adaptedFromFeedback: relevantFeedback.length > 0,
        sessionCorrections: relevantFeedback.length,
        confidenceBoost: this.calculateConfidenceBoost(relevantFeedback),
        lastAdaptation: relevantFeedback[relevantFeedback.length - 1]?.timestamp || '',
        learningHistory: relevantFeedback.map(f => ({
          timestamp: f.timestamp,
          feedback: f.feedback.rating,
          adaptation: this.summarizeAdaptation(f.feedback),
          effectiveness: f.feedback.satisfaction
        }))
      }
    };
  }

  /**
   * Generate enhanced prompt with real-time learning
   */
  enhancePrompt(
    basePrompt: string,
    conversationId: string,
    agentType: AgentType,
    client?: Client
  ): string {
    const context = this.getLearningContext(conversationId, agentType);
    
    if (!context.metadata.adaptedFromFeedback) {
      return basePrompt;
    }

    let enhancedPrompt = basePrompt;

    // Add adaptation instructions
    if (Object.keys(context.adaptations).length > 0) {
      enhancedPrompt += '\n\nREAL-TIME ADAPTATIONS (based on recent user feedback):';
      
      if (context.adaptations.tone) {
        enhancedPrompt += `\n- Adjust tone to be more ${context.adaptations.tone}`;
      }
      
      if (context.adaptations.length) {
        enhancedPrompt += `\n- Adjust response length to be ${context.adaptations.length}`;
      }
      
      if (context.adaptations.style) {
        enhancedPrompt += `\n- Modify style: ${context.adaptations.style}`;
      }
      
      if (context.adaptations.avoid && context.adaptations.avoid.length > 0) {
        enhancedPrompt += `\n- Avoid these patterns: ${context.adaptations.avoid.join(', ')}`;
      }
      
      if (context.adaptations.include && context.adaptations.include.length > 0) {
        enhancedPrompt += `\n- Include these elements: ${context.adaptations.include.join(', ')}`;
      }
    }

    // Add pattern learning
    if (context.patterns.commonCorrections?.length > 0) {
      enhancedPrompt += `\n\nCOMMON USER CORRECTIONS in this session:`;
      context.patterns.commonCorrections.slice(0, 3).forEach(correction => {
        enhancedPrompt += `\n- ${correction}`;
      });
    }

    // Add client-specific adaptations
    if (client && context.patterns.preferredTone) {
      enhancedPrompt += `\n\nCLIENT-SPECIFIC ADAPTATIONS:`;
      enhancedPrompt += `\n- User prefers ${context.patterns.preferredTone} tone for ${client.name}`;
    }

    return enhancedPrompt;
  }

  /**
   * Clear session context (call when conversation ends)
   */
  clearSession(conversationId: string): void {
    this.sessionContexts.delete(conversationId);
  }

  /**
   * Get session learning statistics
   */
  getSessionStats(conversationId: string): {
    totalFeedback: number;
    positiveFeedback: number;
    negativeFeedback: number;
    adaptations: number;
    learningRate: number;
  } {
    const context = this.sessionContexts.get(conversationId);
    
    if (!context) {
      return {
        totalFeedback: 0,
        positiveFeedback: 0,
        negativeFeedback: 0,
        adaptations: 0,
        learningRate: 0
      };
    }

    const totalFeedback = context.feedback.length;
    const positiveFeedback = context.feedback.filter(f => f.feedback.rating === 'positive').length;
    const negativeFeedback = context.feedback.filter(f => f.feedback.rating === 'negative').length;
    
    return {
      totalFeedback,
      positiveFeedback,
      negativeFeedback,
      adaptations: Object.keys(context.activeAdaptations).length,
      learningRate: totalFeedback > 0 ? positiveFeedback / totalFeedback : 0
    };
  }

  // Firestore persistence methods
  private async persistFeedbackToFirestore(
    userId: string,
    conversationId: string,
    messageId: string,
    feedback: InteractionFeedback,
    agentType: AgentType,
    originalResponse: string,
    learningResult: any,
    clientId?: string
  ): Promise<void> {
    try {
      const feedbackId = `${conversationId}_${messageId}_${Date.now()}`;
      const feedbackRef = doc(db, 'users', userId, 'feedback', feedbackId);
      
      await setDoc(feedbackRef, {
        sessionId: conversationId,
        conversationId,
        messageId,
        clientId: clientId || null,
        agentType,
        feedback,
        originalResponse,
        learningResult,
        timestamp: serverTimestamp(),
        processed: false
      });
    } catch (error) {
      console.error('Error persisting feedback to Firestore:', error);
    }
  }

  private async persistSessionToFirestore(
    userId: string,
    conversationId: string,
    sessionContext: SessionLearningContext,
    clientId?: string
  ): Promise<void> {
    try {
      const sessionRef = doc(db, 'users', userId, 'sessions', conversationId);
      
      // Calculate statistics
      const totalFeedback = sessionContext.feedback.length;
      const positiveFeedback = sessionContext.feedback.filter(f => f.feedback.rating === 'positive').length;
      const negativeFeedback = sessionContext.feedback.filter(f => f.feedback.rating === 'negative').length;
      const modifiedFeedback = sessionContext.feedback.filter(f => f.feedback.rating === 'modified').length;
      
      const sessionDoc = {
        conversationId,
        clientId: clientId || null,
        userId,
        updatedAt: serverTimestamp(),
        isActive: true,
        patterns: sessionContext.patterns,
        activeAdaptations: sessionContext.activeAdaptations,
        stats: {
          totalFeedback,
          positiveFeedback,
          negativeFeedback,
          modifiedFeedback,
          adaptationCount: Object.keys(sessionContext.activeAdaptations).length,
          learningRate: totalFeedback > 0 ? positiveFeedback / totalFeedback : 0
        },
        lastActivity: serverTimestamp()
      };

      // Check if document exists to set createdAt only once
      const existingDoc = await getDoc(sessionRef);
      if (!existingDoc.exists()) {
        (sessionDoc as any).createdAt = serverTimestamp();
      }

      await setDoc(sessionRef, sessionDoc, { merge: true });
    } catch (error) {
      console.error('Error persisting session to Firestore:', error);
    }
  }

  private async loadSessionFromFirestore(
    userId: string,
    conversationId: string
  ): Promise<SessionLearningContext | null> {
    try {
      const sessionRef = doc(db, 'users', userId, 'sessions', conversationId);
      const sessionSnap = await getDoc(sessionRef);
      
      if (!sessionSnap.exists()) {
        return null;
      }
      
      const data = sessionSnap.data();
      
      // Load feedback for this session
      const feedbackQuery = query(
        collection(db, 'users', userId, 'feedback'),
        where('sessionId', '==', conversationId),
        orderBy('timestamp', 'asc')
      );
      
      const feedbackSnap = await getDocs(feedbackQuery);
      const feedback = feedbackSnap.docs.map(doc => {
        const feedbackData = doc.data();
        return {
          messageId: feedbackData.messageId,
          timestamp: feedbackData.timestamp.toDate().toISOString(),
          feedback: feedbackData.feedback,
          agentType: feedbackData.agentType,
          originalResponse: feedbackData.originalResponse,
          improvedResponse: feedbackData.improvedResponse
        };
      });
      
      return {
        conversationId,
        feedback,
        patterns: data.patterns || {
          commonCorrections: [],
          preferredTone: '',
          preferredLength: '',
          avoidPatterns: []
        },
        activeAdaptations: data.activeAdaptations || {}
      };
    } catch (error) {
      console.error('Error loading session from Firestore:', error);
      return null;
    }
  }

  /**
   * Load session context from Firestore or memory
   */
  async loadSession(conversationId: string, userId?: string): Promise<void> {
    const uid = userId || this.currentUserId;
    if (!uid) return;

    // Check if already loaded in memory
    if (this.sessionContexts.has(conversationId)) {
      return;
    }

    // Try to load from Firestore
    const sessionContext = await this.loadSessionFromFirestore(uid, conversationId);
    if (sessionContext) {
      this.sessionContexts.set(conversationId, sessionContext);
    }
  }

  /**
   * Mark session as inactive in Firestore
   */
  async deactivateSession(conversationId: string, userId?: string): Promise<void> {
    const uid = userId || this.currentUserId;
    if (!uid) return;

    try {
      const sessionRef = doc(db, 'users', uid, 'sessions', conversationId);
      // Use setDoc with merge to create document if it doesn't exist
      await setDoc(sessionRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error deactivating session:', error);
    }
  }

  /**
   * Get global statistics from Firestore
   */
  async getGlobalLearningStatsFromFirestore(clientId?: string): Promise<any> {
    try {
      if (!this.currentUserId) {
        return this.getGlobalLearningStats(clientId); // Fallback to memory
      }

      // Get active sessions from Firestore
      const sessionsQuery = query(
        collection(db, 'users', this.currentUserId, 'sessions'),
        where('isActive', '==', true),
        ...(clientId ? [where('clientId', '==', clientId)] : [])
      );
      
      const sessionsSnap = await getDocs(sessionsQuery);
      const sessions = sessionsSnap.docs.map(doc => doc.data());
      
      // Calculate statistics
      const totalActiveSessions = sessions.length;
      const totalAdaptations = sessions.reduce((sum, session) => 
        sum + (session.stats?.adaptationCount || 0), 0
      );
      const totalFeedbackItems = sessions.reduce((sum, session) => 
        sum + (session.stats?.totalFeedback || 0), 0
      );
      const totalPositiveFeedback = sessions.reduce((sum, session) => 
        sum + (session.stats?.positiveFeedback || 0), 0
      );
      
      // Get recent activity
      const recentActivityQuery = query(
        collection(db, 'users', this.currentUserId, 'feedback'),
        ...(clientId ? [where('clientId', '==', clientId)] : []),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      
      const activitySnap = await getDocs(recentActivityQuery);
      const recentActivity = activitySnap.docs.map(doc => {
        const data = doc.data();
        return {
          timestamp: data.timestamp.toDate().toISOString(),
          sessionId: data.sessionId,
          adaptationType: Object.keys(data.learningResult?.adaptations || {})[0] || 'feedback',
          feedback: data.feedback.rating
        };
      });
      
      return {
        totalActiveSessions,
        totalAdaptations,
        averageAdaptationsPerSession: totalActiveSessions > 0 ? totalAdaptations / totalActiveSessions : 0,
        totalFeedbackItems,
        globalLearningRate: totalFeedbackItems > 0 ? totalPositiveFeedback / totalFeedbackItems : 0,
        mostCommonAdaptations: [], // TODO: Calculate from adaptations collection
        sessionsByClient: {}, // TODO: Calculate from sessions
        recentActivity
      };
    } catch (error) {
      console.error('Error getting global stats from Firestore:', error);
      return this.getGlobalLearningStats(clientId); // Fallback to memory
    }
  }

  // Private helper methods
  private getOrCreateSessionContext(conversationId: string): SessionLearningContext {
    if (!this.sessionContexts.has(conversationId)) {
      this.sessionContexts.set(conversationId, {
        conversationId,
        feedback: [],
        patterns: {
          commonCorrections: [],
          preferredTone: '',
          preferredLength: '',
          avoidPatterns: []
        },
        activeAdaptations: {}
      });
    }
    return this.sessionContexts.get(conversationId)!;
  }

  private analyzeFeedback(
    feedback: InteractionFeedback,
    originalResponse: string,
    sessionContext: SessionLearningContext
  ): {
    suggestions: string[];
    confidenceBoost: number;
    adaptations: Record<string, any>;
  } {
    const suggestions: string[] = [];
    let confidenceBoost = 0;
    const adaptations: Record<string, any> = {};

    // Analyze based on feedback type
    switch (feedback.rating) {
      case 'positive':
        confidenceBoost = 0.1;
        suggestions.push('Continue with similar approach');
        break;
        
      case 'negative':
        confidenceBoost = -0.2;
        suggestions.push('Significantly modify approach');
        
        // Analyze common issues
        if (originalResponse.length > 1000) {
          adaptations.length = 'shorter';
          suggestions.push('Try shorter responses');
        }
        
        if (originalResponse.includes('I understand') || originalResponse.includes('I apologize')) {
          adaptations.tone = 'more direct';
          suggestions.push('Be more direct, less apologetic');
        }
        break;
        
      case 'modified':
        confidenceBoost = -0.1;
        
        if (feedback.realTimeLearning?.correctiveContext) {
          suggestions.push('Learn from user correction');
          adaptations.include = [feedback.realTimeLearning.correctiveContext];
        }
        
        if (feedback.realTimeLearning?.specificIssues) {
          adaptations.avoid = feedback.realTimeLearning.specificIssues;
          suggestions.push(`Avoid: ${feedback.realTimeLearning.specificIssues.join(', ')}`);
        }
        break;
    }

    // Analyze feedback details for more specific adaptations
    if (feedback.details) {
      if (feedback.details.toLowerCase().includes('too formal')) {
        adaptations.tone = 'more casual';
      } else if (feedback.details.toLowerCase().includes('too casual')) {
        adaptations.tone = 'more formal';
      }
      
      if (feedback.details.toLowerCase().includes('too long')) {
        adaptations.length = 'shorter';
      } else if (feedback.details.toLowerCase().includes('too short')) {
        adaptations.length = 'longer';
      }
    }

    return { suggestions, confidenceBoost, adaptations };
  }

  private async generateAdaptations(
    analysis: any,
    agentType: AgentType,
    client?: Client
  ): Promise<Record<string, any>> {
    const adaptations = { ...analysis.adaptations };

    // Agent-type specific adaptations
    switch (agentType) {
      case 'crisis':
        if (analysis.suggestions.includes('more direct')) {
          adaptations.style = 'emergency response tone';
        }
        break;
        
      case 'media':
        if (adaptations.tone === 'more formal') {
          adaptations.style = 'press release format';
        }
        break;
        
      case 'social':
        if (adaptations.length === 'shorter') {
          adaptations.format = 'tweet-style';
        }
        break;
    }

    // Client-specific adaptations
    if (client) {
      if (client.context.brandTone && adaptations.tone) {
        adaptations.clientTone = client.context.brandTone;
      }
    }

    return adaptations;
  }

  private updateSessionPatterns(
    sessionContext: SessionLearningContext,
    analysis: any
  ): void {
    // Update common corrections
    analysis.suggestions.forEach((suggestion: string) => {
      if (!sessionContext.patterns.commonCorrections.includes(suggestion)) {
        sessionContext.patterns.commonCorrections.push(suggestion);
      }
    });

    // Update preferred tone
    if (analysis.adaptations.tone) {
      sessionContext.patterns.preferredTone = analysis.adaptations.tone;
    }

    // Update preferred length
    if (analysis.adaptations.length) {
      sessionContext.patterns.preferredLength = analysis.adaptations.length;
    }

    // Update avoid patterns
    if (analysis.adaptations.avoid) {
      sessionContext.patterns.avoidPatterns.push(...analysis.adaptations.avoid);
    }

    // Update active adaptations
    Object.assign(sessionContext.activeAdaptations, analysis.adaptations);
  }

  private calculateConfidenceBoost(feedback: any[]): number {
    if (feedback.length === 0) return 0;
    
    const recentFeedback = feedback.slice(-5); // Last 5 feedback items
    const positiveCount = recentFeedback.filter(f => f.feedback.rating === 'positive').length;
    const negativeCount = recentFeedback.filter(f => f.feedback.rating === 'negative').length;
    
    return (positiveCount - negativeCount) * 0.1;
  }

  private summarizeAdaptation(feedback: InteractionFeedback): string {
    if (feedback.realTimeLearning?.correctiveContext) {
      return `User provided: ${feedback.realTimeLearning.correctiveContext}`;
    }
    
    if (feedback.details) {
      return `User noted: ${feedback.details}`;
    }
    
    return `${feedback.rating} feedback`;
  }

  /**
   * Get aggregate statistics across all active sessions for analytics
   */
  getGlobalLearningStats(clientId?: string): {
    totalActiveSessions: number;
    totalAdaptations: number;
    averageAdaptationsPerSession: number;
    totalFeedbackItems: number;
    globalLearningRate: number;
    mostCommonAdaptations: Array<{ type: string; count: number }>;
    sessionsByClient: Record<string, number>;
    recentActivity: Array<{
      timestamp: string;
      sessionId: string;
      adaptationType: string;
      feedback: string;
    }>;
  } {
    const allSessions = Array.from(this.sessionContexts.values());
    const filteredSessions = clientId 
      ? allSessions.filter(session => 
          session.feedback.some(f => f.agentType === clientId) // Approximate client filtering
        )
      : allSessions;

    if (filteredSessions.length === 0) {
      return {
        totalActiveSessions: 0,
        totalAdaptations: 0,
        averageAdaptationsPerSession: 0,
        totalFeedbackItems: 0,
        globalLearningRate: 0,
        mostCommonAdaptations: [],
        sessionsByClient: {},
        recentActivity: []
      };
    }

    // Calculate statistics
    const totalAdaptations = filteredSessions.reduce(
      (sum, session) => sum + Object.keys(session.activeAdaptations).length, 0
    );
    
    const totalFeedbackItems = filteredSessions.reduce(
      (sum, session) => sum + session.feedback.length, 0
    );
    
    const totalPositiveFeedback = filteredSessions.reduce(
      (sum, session) => sum + session.feedback.filter(f => f.feedback.rating === 'positive').length, 0
    );

    // Count adaptation types
    const adaptationCounts: Record<string, number> = {};
    filteredSessions.forEach(session => {
      Object.keys(session.activeAdaptations).forEach(adaptationType => {
        adaptationCounts[adaptationType] = (adaptationCounts[adaptationType] || 0) + 1;
      });
      
      // Also count common corrections
      session.patterns.commonCorrections.forEach(correction => {
        const type = this.categorizeCorrection(correction);
        adaptationCounts[type] = (adaptationCounts[type] || 0) + 1;
      });
    });

    const mostCommonAdaptations = Object.entries(adaptationCounts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Session distribution by agent type (approximating client)
    const sessionsByClient: Record<string, number> = {};
    filteredSessions.forEach(session => {
      session.feedback.forEach(f => {
        sessionsByClient[f.agentType] = (sessionsByClient[f.agentType] || 0) + 1;
      });
    });

    // Recent activity (last 10 items)
    const recentActivity = filteredSessions
      .flatMap(session => 
        session.feedback.map(f => ({
          timestamp: f.timestamp,
          sessionId: session.conversationId,
          adaptationType: Object.keys(session.activeAdaptations)[0] || 'feedback',
          feedback: f.feedback.rating
        }))
      )
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);

    return {
      totalActiveSessions: filteredSessions.length,
      totalAdaptations,
      averageAdaptationsPerSession: filteredSessions.length > 0 
        ? totalAdaptations / filteredSessions.length : 0,
      totalFeedbackItems,
      globalLearningRate: totalFeedbackItems > 0 
        ? totalPositiveFeedback / totalFeedbackItems : 0,
      mostCommonAdaptations,
      sessionsByClient,
      recentActivity
    };
  }

  /**
   * Get detailed session information for analytics
   */
  getActiveSessionsDetails(clientId?: string): Array<{
    conversationId: string;
    adaptations: Record<string, any>;
    feedbackCount: number;
    learningRate: number;
    lastActivity: string;
    patterns: {
      commonCorrections: string[];
      preferredTone: string;
      preferredLength: string;
    };
  }> {
    const allSessions = Array.from(this.sessionContexts.values());
    const filteredSessions = clientId 
      ? allSessions.filter(session => 
          session.feedback.some(f => f.agentType === clientId)
        )
      : allSessions;

    return filteredSessions.map(session => {
      const positiveFeedback = session.feedback.filter(f => f.feedback.rating === 'positive').length;
      const totalFeedback = session.feedback.length;
      
      return {
        conversationId: session.conversationId,
        adaptations: session.activeAdaptations,
        feedbackCount: totalFeedback,
        learningRate: totalFeedback > 0 ? positiveFeedback / totalFeedback : 0,
        lastActivity: session.feedback[session.feedback.length - 1]?.timestamp || '',
        patterns: {
          commonCorrections: session.patterns.commonCorrections,
          preferredTone: session.patterns.preferredTone,
          preferredLength: session.patterns.preferredLength
        }
      };
    });
  }

  /**
   * Get learning insights for analytics dashboard
   */
  getLearningInsights(): {
    insights: Array<{
      type: 'success' | 'warning' | 'info';
      title: string;
      description: string;
      metrics?: Record<string, number>;
    }>;
    recommendations: string[];
  } {
    const stats = this.getGlobalLearningStats();
    const insights: Array<{
      type: 'success' | 'warning' | 'info';
      title: string;
      description: string;
      metrics?: Record<string, number>;
    }> = [];
    const recommendations: string[] = [];

    // Active learning insight
    if (stats.totalActiveSessions > 0) {
      insights.push({
        type: 'info',
        title: 'Real-Time Learning Active',
        description: `${stats.totalActiveSessions} active conversation(s) with ${stats.totalAdaptations} adaptations`,
        metrics: {
          'Active Sessions': stats.totalActiveSessions,
          'Total Adaptations': stats.totalAdaptations,
          'Avg per Session': Math.round(stats.averageAdaptationsPerSession * 10) / 10
        }
      });
    }

    // Learning rate insight
    if (stats.globalLearningRate > 0.8) {
      insights.push({
        type: 'success',
        title: 'High Learning Success Rate',
        description: `${Math.round(stats.globalLearningRate * 100)}% of feedback is positive, indicating effective real-time adaptations`,
        metrics: {
          'Success Rate': Math.round(stats.globalLearningRate * 100),
          'Total Feedback': stats.totalFeedbackItems
        }
      });
    } else if (stats.globalLearningRate < 0.5 && stats.totalFeedbackItems > 5) {
      insights.push({
        type: 'warning',
        title: 'Learning Opportunities',
        description: `${Math.round(stats.globalLearningRate * 100)}% success rate suggests room for improvement`,
        metrics: {
          'Success Rate': Math.round(stats.globalLearningRate * 100),
          'Negative Feedback': stats.totalFeedbackItems - Math.round(stats.globalLearningRate * stats.totalFeedbackItems)
        }
      });
      recommendations.push('Consider providing more specific feedback to help AI learn your preferences');
    }

    // Most common adaptations insight
    if (stats.mostCommonAdaptations.length > 0) {
      const topAdaptation = stats.mostCommonAdaptations[0];
      insights.push({
        type: 'info',
        title: 'Most Common Adaptation',
        description: `"${topAdaptation.type}" adaptations are most frequently applied`,
        metrics: {
          [topAdaptation.type]: topAdaptation.count,
          'Other Types': stats.mostCommonAdaptations.slice(1).reduce((sum, a) => sum + a.count, 0)
        }
      });
    }

    // Recommendations
    if (stats.totalActiveSessions === 0) {
      recommendations.push('Start a conversation to see real-time learning in action');
    }
    
    if (stats.averageAdaptationsPerSession < 1 && stats.totalActiveSessions > 0) {
      recommendations.push('Provide more feedback during conversations to enable more learning');
    }

    if (stats.mostCommonAdaptations.length > 3) {
      recommendations.push('Consider creating reusable templates for commonly adapted patterns');
    }

    return { insights, recommendations };
  }

  // Private helper method for categorizing corrections
  private categorizeCorrection(correction: string): string {
    const lower = correction.toLowerCase();
    if (lower.includes('tone') || lower.includes('formal') || lower.includes('casual')) {
      return 'tone-adjustment';
    }
    if (lower.includes('short') || lower.includes('long') || lower.includes('length')) {
      return 'length-optimization';
    }
    if (lower.includes('direct') || lower.includes('style')) {
      return 'style-modification';
    }
    if (lower.includes('avoid') || lower.includes('don\'t')) {
      return 'pattern-avoidance';
    }
    return 'general-feedback';
  }

  // Public methods for configuration
  setLearningEnabled(enabled: boolean): void {
    this.learningEnabled = enabled;
  }

  isLearningEnabled(): boolean {
    return this.learningEnabled;
  }
}

export const realTimeLearningService = new RealTimeLearningService();
export { RealTimeLearningService };